import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, DollarSign, Building2, Bookmark, BookmarkCheck, Star, Landmark, BadgeCheck, Zap, ExternalLink } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { formatDate, formatSalary, getLicenseBadgeClass } from '../lib/utils';

const JobCard = ({ job, onSave, onUnsave, isSaved = false, showActions = true }) => {
  const {
    job_id,
    title,
    company_name,
    location,
    job_type,
    license_required,
    salary_min,
    salary_max,
    pay_frequency,
    is_remote,
    is_featured,
    is_government,
    government_level,
    is_verified_employer,
    urgently_hiring,
    employer_logo_url,
    created_at,
    application_count,
    application_type,
    is_external,
    sw_classification,
  } = job;

  const isExternalApply = is_external || application_type === 'external';

  const handleSaveClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (isSaved && onUnsave) {
      onUnsave(job_id);
    } else if (onSave) {
      onSave(job_id);
    }
  };

  return (
    <Link
      to={`/jobs/${job_id}`}
      className={`block job-card bg-white border-2 rounded-lg p-5 sm:p-6 transition-all group ${
        is_featured 
          ? 'border-secondary/50 shadow-md shadow-secondary/10 hover:border-secondary' 
          : 'border-gray-100 hover:border-secondary/50'
      }`}
      data-testid={`job-card-${job_id}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {/* Featured Badge */}
          {is_featured && (
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Badge className="bg-secondary text-white text-xs font-medium">
                <Star className="w-3 h-3 mr-1 fill-current" />
                Featured
              </Badge>
              {urgently_hiring && (
                <Badge className="bg-red-600 text-white text-xs font-medium" data-testid={`urgently-hiring-${job_id}`}>
                  <Zap className="w-3 h-3 mr-1 fill-current" />
                  Urgently Hiring
                </Badge>
              )}
            </div>
          )}
          {!is_featured && urgently_hiring && (
            <div className="mb-3">
              <Badge className="bg-red-600 text-white text-xs font-medium" data-testid={`urgently-hiring-${job_id}`}>
                <Zap className="w-3 h-3 mr-1 fill-current" /> Urgently Hiring
              </Badge>
            </div>
          )}
          
          {/* Government Badge */}
          {is_government && (
            <div className="mb-3">
              <Badge className="bg-blue-600 text-white text-xs font-medium">
                <Landmark className="w-3 h-3 mr-1" />
                {government_level === 'federal' ? 'Federal Job via USAJobs.gov' : 'Government Job'}
              </Badge>
            </div>
          )}
          
          {/* Header */}
          <div className="flex items-start gap-3 mb-3">
            <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden ${
              is_featured ? 'bg-secondary/10' : 'bg-primary/10'
            }`}>
              {employer_logo_url ? (
                <img src={employer_logo_url} alt={company_name} className="w-full h-full object-contain" data-testid={`employer-logo-${job_id}`} />
              ) : (
                <Building2 className={`w-5 h-5 sm:w-6 sm:h-6 ${is_featured ? 'text-secondary' : 'text-primary'}`} />
              )}
            </div>
            <div className="min-w-0">
              <h3 className={`font-serif text-lg font-semibold text-gray-900 transition-colors truncate ${
                is_featured ? 'group-hover:text-secondary' : 'group-hover:text-primary'
              }`}>
                {title}
              </h3>
              <div className="flex items-center gap-1.5">
                <p className="text-gray-600 text-sm truncate">{company_name}</p>
                {is_verified_employer && (
                  <span
                    className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary shrink-0"
                    title="Verified Employer"
                    data-testid={`verified-employer-badge-${job_id}`}
                  >
                    <BadgeCheck className="w-3.5 h-3.5" />
                    Verified
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <Badge className={`${getLicenseBadgeClass(license_required)} text-xs font-medium`}>
              {license_required}
            </Badge>
            <Badge variant="outline" className="text-xs">
              {job_type.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
            </Badge>
            {is_remote && (
              <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                Remote
              </Badge>
            )}
            {isExternalApply ? (
              <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700" data-testid={`apply-badge-external-${job_id}`}>
                <ExternalLink className="w-3 h-3 mr-1" />
                External
              </Badge>
            ) : (
              <Badge variant="secondary" className="text-xs bg-green-100 text-green-700" data-testid={`apply-badge-easy-${job_id}`}>
                <Zap className="w-3 h-3 mr-1" />
                Easy Apply
              </Badge>
            )}
          </div>

          {/* Details */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {location}
            </span>
            {(salary_min || salary_max) && (
              <span className="flex items-center gap-1 font-semibold text-primary" data-testid="job-card-salary">
                <DollarSign className="w-4 h-4" />
                {formatSalary(salary_min, salary_max)}
                {pay_frequency && <span className="font-normal text-gray-500">/{{ year: 'yr', month: 'mo', week: 'wk', hour: 'hr', visit: 'visit' }[pay_frequency] || pay_frequency}</span>}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {formatDate(created_at)}
            </span>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div className="flex sm:flex-col items-center gap-2 sm:gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleSaveClick}
              className={`h-9 w-9 ${isSaved ? 'text-secondary hover:text-secondary-hover' : 'text-gray-400 hover:text-gray-600'}`}
              data-testid={`save-job-${job_id}`}
            >
              {isSaved ? (
                <BookmarkCheck className="w-5 h-5 fill-current" />
              ) : (
                <Bookmark className="w-5 h-5" />
              )}
            </Button>
            {application_count > 0 && (
              <span className="text-xs text-gray-400">
                {application_count} {application_count === 1 ? 'applicant' : 'applicants'}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
};

export default JobCard;
